import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Grid,
  Typography,
  Button,
  Snackbar,
  TextField,
  CircularProgress,
  LinearProgress,
  Box,
} from '@mui/material';

import { register } from 'js/library/utils/API/auth/register';
import { login } from 'js/library/utils/API/auth/login';
import { requestCode } from 'js/library/utils/API/auth/requestCode';
import { loginRedirectKey } from 'js/library/utils/helpers';
import { getFingerprint } from 'js/library/utils/getFingerprint';
import { useAuth } from 'js/contexts/AuthContext';
import cfac22 from 'js/library/utils/cfac22';
import phoneMaskArray from './phoneMaskArray.json';

const CODE_LENGTH = 6;
const RESEND_TIMEOUT_SECONDS = 30;
const DEFAULT_SEND_CODE_TYPE = { sms: false, whatsapp: true };

const countryMasksByCodeLength = [...phoneMaskArray].sort(
  (countryA, countryB) =>
    countryB.ddd.replace(/\D/g, '').length - countryA.ddd.replace(/\D/g, '').length
);

const applyMaskedDigitsToMask = (maskedDigits, mask) => {
  let maskedPhone = '';
  let digitIndex = 0;

  for (const maskChar of mask) {
    if (maskChar === '9') {
      if (digitIndex >= maskedDigits.length) break;

      maskedPhone += maskedDigits[digitIndex];
      digitIndex += 1;
    } else {
      maskedPhone += maskChar;
    }
  }

  return maskedPhone;
};

const maskPhoneNumber = (phoneNumber = '') => {
  const digits = String(phoneNumber).replace(/\D/g, '');
  const countryMask = countryMasksByCodeLength.find((country) =>
    digits.startsWith(country.ddd.replace(/\D/g, ''))
  );
  const countryCode = countryMask?.ddd || '';
  const countryCodeLength = countryCode.replace(/\D/g, '').length;
  const nationalDigits = countryMask ? digits.slice(countryCodeLength) : digits;
  const visibleDigits = nationalDigits.slice(-4);
  const hiddenDigits = '*'.repeat(Math.max(nationalDigits.length - visibleDigits.length, 0));
  const maskedDigits = `${hiddenDigits}${visibleDigits}`;

  if (countryMask?.mask) {
    return `${countryCode} ${applyMaskedDigitsToMask(maskedDigits, countryMask.mask)}`.trim();
  }

  return maskedDigits;
};

function CodeInput({ cellPhoneProps, transactionIdProps }) {
  const { t } = useTranslation('app');
  const navigate = useNavigate();
  const { authState, setAuthData, enrichAfterAuthentication, setRequestCodeAuth } = useAuth();

  const [code, setCode] = useState('');
  const cellPhone = cellPhoneProps
    ? cellPhoneProps
    : authState.requestCode?.cellPhone || authState.identify?.identifier || '';
  const initialRequestCodeId = transactionIdProps || authState.requestCode?.id || null;

  // Detecta se é um novo usuário baseado apenas no requestCode (vem do identify)
  // Se found: true no identify → newUser: false → chama login
  // Se found: false no identify → newUser: true → vai para cadastro → chama register
  const isNewUser = authState.requestCode?.newUser === true;

  const navigateAfterLogin = () => {
    const redirectDirectory = sessionStorage.getItem(loginRedirectKey);
    sessionStorage.removeItem(loginRedirectKey);
    navigate(redirectDirectory ? decodeURIComponent(redirectDirectory) : '/');
  };

  useEffect(() => {
    if (!authState.identify?.identifier) navigate('/login', { replace: true });
  }, [authState.identify?.identifier, navigate]);

  const [loading, setLoading] = useState(false);
  const [renderTime, setRenderTime] = useState(0);
  const [messageSnackbar, setMessageSnackbar] = useState(t('errors.user_not_logged_in'));
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [startTimer, setStartTimer] = useState(true);
  const [resendCode, setResendCode] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [timer, setTimer] = useState(RESEND_TIMEOUT_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [sendCodeType, setSendCodeType] = useState(DEFAULT_SEND_CODE_TYPE);
  const [disabledInput, setDisabledInput] = useState(false);
  useEffect(() => {
    window.pathname = null;
  }, []);

  useEffect(() => {
    if (!cellPhone) {
      navigate('/login');
    }
  }, [cellPhone, navigate]);

  useEffect(() => {
    let timerInterval = null;
    if (timer > 0 && !canResend) {
      timerInterval = setInterval(() => {
        setTimer((timer) => timer - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timerInterval);
  }, [timer, canResend]);

  useEffect(() => {
    let progressTimer = null;

    progressTimer = setInterval(() => {
      if (renderTime < 100 && startTimer) {
        setRenderTime((renderTime) => renderTime + 0.25);
      } else {
        clearInterval(progressTimer);
        setStartTimer(false);
        setRenderTime(0);
      }
    }, 60);

    return () => clearInterval(progressTimer);
  }, [renderTime, startTimer]);

  const validateSMS = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const partnerId = cfac22('partnerId');
      const identifier =
        authState.identify?.identifier ||
        authState.requestCode?.cellPhone ||
        authState.registrationDraft?.cellphone ||
        cellPhone;
      const fingerprint = await getFingerprint();

      // Verifica se é novo usuário (cadastro) ou usuário existente (login)
      if (isNewUser) {
        const draft = authState.registrationDraft || {};
        const dynamicData = draft.dynamicData || {};
        const dataWithoutEmptyFields = Object.fromEntries(
          Object.entries(dynamicData).filter(
            ([, value]) => value !== '' && value !== undefined && value !== null
          )
        );
        const data = {
          ...dataWithoutEmptyFields,
          ...(cellPhone
            ? {
                contactList: [
                  ...(dynamicData.contactList || []).filter((item) => item?.type !== 'cellPhone'),
                  { type: 'cellPhone', value: cellPhone },
                ],
              }
            : {}),
        };
        const payload = {
          identifier,
          cellPhone,
          partnerId,
          code,
          clientType: 'web',
          fingerprint,
          data,
        };

        const response = await register(payload);

        if (!response.success) {
          setLoading(false);
          setOpenSnackbar(true);
          setMessageSnackbar(response.error || t('errors.try_later'));
          return;
        }

        await setAuthData(response.data);
        enrichAfterAuthentication({ authData: response.data, partnerId, identifier });

        toast.success('Cadastro realizado com sucesso!');
        navigate('/');
      } else {
        const response = await login({
          identifier,
          partnerId,
          code: code,
          clientType: 'web',
          fingerprint,
        });

        if (!response.success) {
          setLoading(false);
          setOpenSnackbar(true);
          setMessageSnackbar(response.error || t('errors.try_later'));
          return;
        }

        await setAuthData(response.data);
        enrichAfterAuthentication({ authData: response.data, partnerId, identifier });

        navigateAfterLogin();
      }
    } catch (error) {
      console.error('ERRO NA VALIDAÇÃO:', error);
      setLoading(false);

      if (error?.response?.status === 422) {
        setOpenSnackbar(true);
        setMessageSnackbar(t('errors.invalid_code'));
      } else {
        setOpenSnackbar(true);
        setMessageSnackbar(error?.error || error?.message || t('errors.try_later'));
      }
    }
  };

  const getNewCode = (sendCodeTypeOptions = DEFAULT_SEND_CODE_TYPE) => {
    setLoadingResend(true);
    setCode('');
    setSendCodeType(sendCodeTypeOptions);

    // Define o sendType baseado na escolha do usuário
    let sendType;
    if (sendCodeTypeOptions.sms) {
      sendType = { whatsapp: false, email: true, sms: true }; // SMS selecionado
    } else {
      sendType = { whatsapp: true, email: true, sms: false }; // WhatsApp selecionado
    }

    requestCode({
      cellPhone,
      identifier: authState.identify?.identifier || cellPhone,
      partnerId: cfac22('partnerId'),
      newUser: isNewUser,
      sendType: sendType.sms ? ['sms'] : ['whatsapp'],
    })
      .then((resultAuth) => {
        if (!resultAuth.error) {
          setRequestCodeAuth({
            cellPhone: resultAuth.cellPhone,
            id: resultAuth.id || initialRequestCodeId,
            newUser: isNewUser,
          });
          setStartTimer(true);
          setResendCode(false);
          setLoadingResend(false);
          setTimer(RESEND_TIMEOUT_SECONDS);
          setCanResend(false);
          setOpenSnackbar(true);
          setMessageSnackbar(t('general.code_sent'));
          setDisabledInput(false);
        } else {
          setLoadingResend(false);
          setOpenSnackbar(true);
          setMessageSnackbar(t('errors.try_later'));
          setDisabledInput(true);
        }
      })
      .catch(() => {
        setLoadingResend(false);
        setOpenSnackbar(true);
        setMessageSnackbar(t('errors.try_later'));
        setDisabledInput(true);
      });
  };

  const handleCodeChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');

    if (value.length <= CODE_LENGTH) {
      setCode(value);
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const handleRequestNewCode = (sendCodeTypeOptions) => {
    getNewCode(sendCodeTypeOptions);
  };

  const deliveryMessage = sendCodeType.whatsapp
    ? t('general.you_will_receive_auth_code_in_seconds')
    : t('general.you_will_receive_auth_code_in_seconds_sms');
  const maskedCellPhone = maskPhoneNumber(cellPhone);
  const resendProgress = ((RESEND_TIMEOUT_SECONDS - timer) / RESEND_TIMEOUT_SECONDS) * 100;
  const showResendCountdown = !canResend;
  const showResendPrompt = canResend && !disabledInput && !resendCode;
  const showResendOptions = resendCode && canResend && !disabledInput;
  const isSubmitDisabled = code.length !== CODE_LENGTH || loading || resendCode;

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      className="background-login"
      sx={{ px: 2, py: 4, textAlign: 'center' }}
    >
      <Box sx={{ width: 500, maxWidth: '100%' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1">{deliveryMessage}</Typography>
          <Typography component="strong" variant="h6" sx={{ display: 'block', mt: 1 }}>
            {maskedCellPhone}
          </Typography>
        </Box>

        <Box
          component="form"
          noValidate
          onSubmit={validateSMS}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            autoComplete="one-time-code"
            type="tel"
            autoFocus
            fullWidth
            id="code"
            value={code}
            onChange={handleCodeChange}
            margin="normal"
            placeholder="999999"
            disabled={disabledInput}
            slotProps={{
              htmlInput: {
                maxLength: CODE_LENGTH,
                pattern: '[0-9]*',
                inputMode: 'numeric',
                'aria-label': t('general.authentication_code'),
                style: {
                  fontSize: '1.7rem',
                  letterSpacing: '6px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  cursor: disabledInput ? 'not-allowed' : 'inherit',
                },
              },
            }}
            sx={{ mt: 0 }}
          />

          {showResendPrompt && (
            <Box>
              <Typography variant="body2">{t('general.didnt_receive_the_code')}</Typography>
              <Button
                type="button"
                variant="text"
                color="primary"
                onClick={() => setResendCode(true)}
                sx={{ px: 1, textTransform: 'none', textDecoration: 'underline' }}
              >
                {t('general.resend_sms')}
              </Button>
            </Box>
          )}

          {showResendCountdown && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={resendProgress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  '& .MuiLinearProgress-bar': {
                    transition: 'transform 1s linear',
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                aria-live="polite"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {t('general.wait_to_request_new_code', { timer })}
              </Typography>
            </Box>
          )}

          {showResendOptions && (
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {t('general.botton-option-send-code')}
              </Typography>
              {loadingResend ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={30} color="primary" />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Button
                      fullWidth
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={() => handleRequestNewCode({ sms: true, whatsapp: false })}
                      sx={{ textTransform: 'none' }}
                    >
                      SMS
                    </Button>
                    <Button
                      fullWidth
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={() => handleRequestNewCode({ sms: false, whatsapp: true })}
                      sx={{ textTransform: 'none' }}
                    >
                      WhatsApp
                    </Button>
                  </Box>
                  <Button
                    color="primary"
                    type="button"
                    fullWidth
                    variant="text"
                    onClick={() => setResendCode(false)}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('general.cancel')}
                  </Button>
                </>
              )}
            </Box>
          )}

          {disabledInput && (
            <Box>
              <Typography variant="caption" color="error">
                {messageSnackbar}
              </Typography>
            </Box>
          )}

          <Button
            disabled={isSubmitDisabled}
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            sx={{ minHeight: 48, textTransform: 'none' }}
          >
            {loading ? (
              <CircularProgress size="2rem" sx={{ color: 'common.white' }} />
            ) : (
              <Typography component="span" variant="h6">
                {t('general.continue')}
              </Typography>
            )}
          </Button>
        </Box>

        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={openSnackbar}
          onClose={handleCloseSnackbar}
          ContentProps={{ 'aria-describedby': 'message-id' }}
          message={<span id="message-id">{messageSnackbar}</span>}
        />
      </Box>
    </Grid>
  );
}

export default CodeInput;
